import { useEffect, useState } from "react";
import { Clock, Crown, Layers, Loader2, Trash2, X } from "lucide-react";
import { api, ApiError } from "../lib/api";
import { DesignState, TemplateOut } from "../types";
import { ScreenHeader } from "./ScreenHeader";
import { LottieThumbnail } from "./LottieThumbnail";
import { openExternalLink } from "../lib/telegram";
import { useDesign } from "../state/designStore";

const SUBSCRIBE_CONTACT_LINK = "https://t.me/hhs9h";
const BLANK_TEMPLATE_ID = "_blank_transparent";

interface ProjectOut {
  id: string;
  name: string;
  template_id: string | null;
  updated_at: number;
  expires_at: number;
  preview: Record<string, unknown> | null;
  design?: DesignState | null;
}

function ago(ts: number, now: number): string {
  const s = Math.max(0, now - ts);
  if (s < 60) return "هسه";
  if (s < 3600) return `قبل ${Math.floor(s / 60)} دقيقة`;
  if (s < 86400) return `قبل ${Math.floor(s / 3600)} ساعة`;
  return `قبل ${Math.floor(s / 86400)} يوم`;
}

function remaining(ts: number, now: number): string {
  const s = Math.max(0, ts - now);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  if (d > 0) return `تنتهي بعد ${d} يوم${h ? ` و${h} ساعة` : ""}`;
  if (h > 0) return `تنتهي بعد ${h} ساعة`;
  return "تنتهي قريباً";
}

export function VipFeatureModal({ onClose }: { onClose: () => void }) {
  const { setOverlayClose } = useDesign();
  useEffect(() => {
    setOverlayClose(() => onClose);
    return () => setOverlayClose(null);
  }, [onClose, setOverlayClose]);

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/60 p-6" onClick={onClose}>
      <div className="glass-card w-full max-w-xs p-5 flex flex-col items-center gap-3 text-center" onClick={(e) => e.stopPropagation()}>
        <span className="w-12 h-12 rounded-full bg-vinyl-sunburst/20 flex items-center justify-center">
          <Crown size={24} className="text-vinyl-sunburst" strokeWidth={2.25} />
        </span>
        <div>
          <span className="block font-bold">آخر المشاريع ميزة VIP</span>
          <span className="block text-sm text-tg-hint mt-1">
            نحفظ لك آخر 3 مشاريع لمدة 3 أيام، ترجع تكمل شغلك من وين وقفت
          </span>
        </div>
        <div className="w-full flex flex-col gap-2 mt-1">
          <button
            type="button"
            onClick={() => openExternalLink(SUBSCRIBE_CONTACT_LINK)}
            className="w-full py-2.5 rounded-xl bg-tg-button text-tg-buttonText font-semibold text-sm"
          >
            تواصل مع الدعم للاشتراك
          </button>
          <button type="button" onClick={onClose} className="w-full py-2.5 rounded-xl glass-pill text-sm font-medium flex items-center justify-center gap-1.5">
            <X size={15} strokeWidth={2.25} />
            إلغاء
          </button>
        </div>
      </div>
    </div>
  );
}

export function RecentProjectsScreen({ onBack, onOpened }: { onBack: () => void; onOpened: (isBlank: boolean) => void }) {
  const { resumeProject } = useDesign();
  const [items, setItems] = useState<ProjectOut[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const now = Math.floor(Date.now() / 1000);

  useEffect(() => {
    api
      .get<ProjectOut[]>("/api/projects")
      .then(setItems)
      .catch((e) => {
        setItems([]);
        setError(e instanceof ApiError && e.status === 403 ? "هاي الميزة لمشتركي VIP بس" : "ما كدرت أحمّل المشاريع");
      });
  }, []);

  const open = async (p: ProjectOut) => {
    if (busyId) return;
    setBusyId(p.id);
    setError(null);
    try {
      const full = await api.get<ProjectOut>(`/api/projects/${encodeURIComponent(p.id)}`);
      if (!full.design || !full.design.template_id) throw new Error("bad");
      const tpl = await api.get<TemplateOut>(`/api/templates/${encodeURIComponent(full.design.template_id)}`);
      resumeProject(full.id, full.design, tpl);
      onOpened(tpl.id === BLANK_TEMPLATE_ID);
    } catch (e) {
      setError(e instanceof ApiError && e.status === 404 ? "المشروع أو قالبه ما عاد موجود" : "ما كدرت أفتح المشروع");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id: string) => {
    setConfirmId(null);
    try {
      await api.del(`/api/projects/${encodeURIComponent(id)}`);
      setItems((cur) => (cur ? cur.filter((x) => x.id !== id) : cur));
    } catch {
      setError("ما كدرت أحذف المشروع");
    }
  };

  return (
    <div className="h-full overflow-y-auto">
      <ScreenHeader title="آخر المشاريع" onBack={onBack} />
      <div className="p-4 flex flex-col gap-3">
        <div className="flex items-center gap-2 text-xs text-tg-hint px-1">
          <Crown size={13} className="text-vinyl-sunburst" />
          <span>نحفظ لك آخر 3 مشاريع، وكل مشروع يبقى 3 أيام من آخر تعديل</span>
        </div>

        {error && <p className="text-red-400 text-sm px-1">{error}</p>}

        {items === null && (
          <div className="flex justify-center py-16 text-tg-hint">
            <Loader2 className="animate-spin" size={22} />
          </div>
        )}

        {items && items.length === 0 && !error && (
          <div className="glass-card p-6 flex flex-col items-center gap-2 text-center">
            <Layers size={28} className="text-tg-hint" />
            <span className="font-bold">ما عندك مشاريع محفوظة</span>
            <span className="text-sm text-tg-hint">أي ملصق تشتغل عليه يتحفظ هنا تلقائياً</span>
          </div>
        )}

        {items?.map((p) => (
          <div key={p.id} className="glass-card p-3 flex items-center gap-3">
            <button type="button" onClick={() => open(p)} className="flex items-center gap-3 flex-1 min-w-0 text-right">
              <span className="shrink-0 relative">
                {p.preview ? (
                  <LottieThumbnail src={p.preview} size={76} />
                ) : (
                  <span className="w-[76px] h-[76px] rounded-xl bg-black/20 flex items-center justify-center">
                    <Layers size={22} className="text-tg-hint" />
                  </span>
                )}
                {busyId === p.id && (
                  <span className="absolute inset-0 rounded-xl bg-black/50 flex items-center justify-center">
                    <Loader2 className="animate-spin" size={20} />
                  </span>
                )}
              </span>
              <span className="flex-1 min-w-0">
                <span className="block font-bold truncate">{p.name}</span>
                <span className="flex items-center gap-1 text-xs text-tg-hint mt-1">
                  <Clock size={12} />
                  {ago(p.updated_at, now)}
                </span>
                <span className="block text-[11px] mt-0.5" style={{ color: "#8FB4FF" }}>
                  {remaining(p.expires_at, now)}
                </span>
              </span>
            </button>
            {confirmId === p.id ? (
              <div className="flex flex-col gap-1.5 shrink-0">
                <button type="button" onClick={() => remove(p.id)} className="px-3 py-1.5 rounded-lg bg-red-500/80 text-white text-xs font-semibold">
                  حذف
                </button>
                <button type="button" onClick={() => setConfirmId(null)} className="px-3 py-1.5 rounded-lg glass-pill text-xs">
                  لا
                </button>
              </div>
            ) : (
              <button
                type="button"
                aria-label="حذف المشروع"
                onClick={() => setConfirmId(p.id)}
                className="w-9 h-9 rounded-full flex items-center justify-center text-tg-hint active:bg-white/10 shrink-0"
              >
                <Trash2 size={17} />
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
